import { Suspense, lazy, memo } from 'react';
import theme from 'styled-theming';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import ScrollAdapt from '~/components/misc/scroll-adapt.js';
import breakpoint, { getZindexByType } from '~/utils/style-utils.ts';
import {
	selectIsDesktop,
	selectBlackFridayCampaignEnabled,
	selectLicense
} from '~/components/brand/ducks/selectors.js';
import { isSmallerSelector } from '~/components/layout/mediaquery.js';
import useForcePinMobileNavbar from '~/hooks/use-force-pin-mobile-headroom.js';
import useUIContext from '~/ui/use-ui-context';
import { LICENSE_DGA } from '~/utils/license-utils';
import useDisplayGlobalNavigation from '~/components/global-navigation/use-display-global-navigation';

const TopNavigation = lazy(() =>
	import(/* webpackChunkName: "global-navigation" */ '~/components/global-navigation/top-navigation/top-navigation')
);

const headerPositionTop = ({ hasStaticHeader }) => {
	const staticHeaderHeight = hasStaticHeader ? 'var(--regulationHeight)' : '0px';
	return `calc(${staticHeaderHeight})`;
};
const headerSmallerHeadroomBrandExceptionStyle = theme('brand', {
	mobilebet: css`
		background: var(--brandContainerBackground);
		background-size: 216px 222px;
	`
});
// eslint-disable-next-line no-undef
const HeaderSmaller = styled(ScrollAdapt)`
	grid-area: header;
	width: 100%;
	position: ${({ uiContext }) => (uiContext.forceAbsoluteHeader ? 'absolute' : 'sticky')};
	top: ${({ uiContext }) => headerPositionTop(uiContext)};
	z-index: ${getZindexByType('small-header')};
	background: var(--head-bg);
	border-bottom: 1px solid var(--head-bg);

	${breakpoint.lg`
		z-index: ${getZindexByType('header')};
	`}

	${headerSmallerHeadroomBrandExceptionStyle}
`;
const HeaderBigger = styled.header`
	grid-area: header;
	width: 100%;
	position: ${({ uiContext }) => (uiContext.forceAbsoluteHeader ? 'absolute' : 'fixed')};
	transform: translateZ(0);
	z-index: ${getZindexByType('header')};
	top: ${({ uiContext }) => (uiContext.hasStaticHeader ? `var(--regulationHeight)` : '0')};
	background: ${({ bg }) => bg};
`;

const TRANSITION = {
	duration: 0.1,
	type: 'tween'
};

const classNames = classes => {
	return classes
		.map(className => className.trim())
		.filter(className => !!className)
		.join(' ');
};

const fixedHeaderLicenses = [LICENSE_DGA];

const keepHeadersFixed = (forcePinHeadroom, license) => {
	if (fixedHeaderLicenses.some(fixedLicense => license === fixedLicense)) {
		forcePinHeadroom();
	}
};

const HeaderWrapper = memo(({ children, hasSmallerBrandingHeader }) => {
	const isDesktop = useSelector(selectIsDesktop);
	const smaller = useSelector(isSmallerSelector);
	const { navbarIsForced: forceHeaderDisplay, forcePinHeadroom } = useForcePinMobileNavbar();
	const blackFridayCampaignEnabled = useSelector(selectBlackFridayCampaignEnabled);
	const uiContext = useUIContext();
	const license = useSelector(selectLicense);
	const { displayGlobalNavigation } = useDisplayGlobalNavigation();

	keepHeadersFixed(forcePinHeadroom, license);

	return smaller ? (
		<Suspense fallback={<div className="spinner" />}>
			{hasSmallerBrandingHeader && (
				<HeaderSmaller
					className={classNames([
						'page-head adaptable-header-main',
						blackFridayCampaignEnabled ? 'black-friday-header' : ''
					])}
					forceVisible={forceHeaderDisplay && !displayGlobalNavigation}
					isDesktop={isDesktop}
					transition={TRANSITION}
					uiContext={uiContext}
				>
					{children}
				</HeaderSmaller>
			)}
			{displayGlobalNavigation && <TopNavigation />}
		</Suspense>
	) : (
		<HeaderBigger
			id="header-bigger"
			dataAt="big-layout-header"
			bg={blackFridayCampaignEnabled ? 'rgba(var(--base-12), 1)' : 'var(--head-bg, rgba(var(--base-09), 1))'}
			uiContext={uiContext}
		>
			{children}
		</HeaderBigger>
	);
});

export default HeaderWrapper;
